import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { API } from '@/lib/api';
import { loadIntegrationsOverview } from '@/lib/workflowData';
import {
  DEFAULT_SIDEBAR_NAV_ORDER,
  SIDEBAR_FIXED_ITEMS,
  SIDEBAR_MOVABLE_ITEMS,
  normalizeSidebarNavOrder,
} from '@/lib/sidebarNav';
import {
  clearGeneratedLocalPassword,
  clearPasswordVerification,
  readGeneratedLocalPassword,
  readPasswordVerification,
} from '@/lib/localPasswordSession';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';
import { AppPageHeader } from '@/components/layout/AppPage';
import { toast } from 'sonner';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Bell,
  BookOpen,
  Bot,
  Brain,
  Building2,
  CheckCircle2,
  CreditCard,
  Crown,
  Download,
  Eye,
  EyeOff,
  Globe,
  Lock,
  LogOut,
  Moon,
  Newspaper,
  Palette,
  Rocket,
  Save,
  Scale,
  Shield,
  Smartphone,
  Sparkles,
  Sun,
  Target,
  TrendingUp,
  Trash2,
  User,
} from 'lucide-react';

const HEADER_FONT = { fontFamily: 'var(--font-display)' };

const SECTIONS = [
  { id: 'overview', icon: Sparkles, label: 'Overview', hint: 'Your legal cockpit' },
  { id: 'profile', icon: User, label: 'Profile', hint: 'Identity and account context' },
  { id: 'experience', icon: Brain, label: 'Workflow', hint: 'Defaults, focus, and appearance' },
  { id: 'notifications', icon: Bell, label: 'Briefings', hint: 'Alerts and daily rhythm' },
  { id: 'integrations', icon: Globe, label: 'Integrations', hint: 'Live, manual, and scaffolded hooks' },
  { id: 'governance', icon: Shield, label: 'Governance', hint: 'Trust labels, source rules, and audit rails' },
  { id: 'membership', icon: CreditCard, label: 'Membership', hint: 'Plan value and perks' },
  { id: 'privacy', icon: Shield, label: 'Privacy', hint: 'Exports and session safety' },
];

const LANGUAGES = ['ENGLISH', 'HINDI', 'TAMIL', 'TELUGU', 'MARATHI', 'BENGALI', 'GUJARATI', 'KANNADA'];
const MODE_OPTIONS = [
  { value: 'RESEARCH', label: 'Research', note: 'Find authorities, issues, and case support quickly.' },
  { value: 'DRAFT', label: 'Draft', note: 'Keep the assistant drafting-forward for clauses and pleadings.' },
  { value: 'EXPLAIN', label: 'Explain', note: 'Ask for plain-language breakdowns and client-ready clarity.' },
  { value: 'STRATEGY', label: 'Strategy', note: 'Use issue trees, next steps, and argument pathways.' },
  { value: 'MOOT_COURT', label: 'Moot Court', note: 'Optimise for oral arguments, structure, and rebuttal prep.' },
  { value: 'EXAM_PREP', label: 'Exam Prep', note: 'Stay concise, structured, and memory-friendly.' },
];
const COURT_OPTIONS = [
  'Auto-detect from matter',
  'Supreme Court of India',
  'Delhi High Court',
  'Bombay High Court',
  'Karnataka High Court',
  'Madras High Court',
  'NCLT',
  'NCLAT',
  'Consumer Commission',
  'Arbitration',
  'District Courts',
];
const PRACTICE_AREA_OPTIONS = [
  'Commercial Disputes',
  'Contracts',
  'Arbitration',
  'Labour & Employment',
  'Corporate Advisory',
  'IP & Technology',
  'Tax',
  'Real Estate',
  'Criminal',
  'Constitutional',
];
const COURT_PREFERENCE_OPTIONS = [
  'Supreme Court',
  'High Courts',
  'District Courts',
  'Tribunals',
  'Arbitration',
  'Regulatory Forums',
  'Consumer Forums',
  'Company Law Benches',
];

const PLAN_META = {
  FREE: {
    title: 'Starter',
    strap: 'A sharp entry point for first matters and lighter workflows.',
    accent: 'rgba(46, 204, 90, 0.16)',
    features: ['50 AI queries / month', '5 documents / month', 'Core research and drafting flows'],
  },
  COMMUNITY: {
    title: 'Community Tier',
    strap: 'Approved feature access for specific cohorts without converting the account into a paid plan.',
    accent: 'rgba(16, 185, 129, 0.16)',
    features: ['Program-specific feature unlocks', 'Community usage limits', 'Access-code based activation'],
  },
  PRO: {
    title: 'Pro Counsel',
    strap: 'Unlimited momentum for heavy drafting, research, and personalisation.',
    accent: 'rgba(59, 130, 246, 0.16)',
    features: ['Unlimited AI queries', 'Unlimited documents', 'Priority access to premium templates'],
  },
  TEAM: {
    title: 'Chambers',
    strap: 'Built for firms and shared institutional workflows.',
    accent: 'rgba(212, 160, 23, 0.16)',
    features: ['Team access', 'Shared workspaces', 'Institution-first knowledge memory'],
  },
};

const TIER_CARDS = [
  {
    key: 'FREE',
    title: 'Starter',
    summary: 'For solo exploration and lighter drafting sessions.',
    features: ['Core chat', 'Research workspace', 'Foundational saved library'],
  },
  {
    key: 'COMMUNITY',
    title: 'Community Tier',
    summary: 'For approved cohorts with access-code based community programs.',
    features: ['Selected agents enabled', 'Program-specific usage limits', 'Not a paid plan'],
  },
  {
    key: 'PRO',
    title: 'Pro Counsel',
    summary: 'For power users who want faster, stickier daily workflows.',
    features: ['Unlimited drafting', 'Premium templates', 'Deeper default personalisation'],
  },
  {
    key: 'TEAM',
    title: 'Chambers',
    summary: 'For firms, teams, and institutions running shared matters.',
    features: ['Shared workspace identity', 'Team-ready access', 'Institution memory'],
  },
];

const WORKFLOW_CARDS = [
  {
    icon: Bot,
    title: 'Research Workspace',
    body: 'Search, brief, and draft in one legal research workflow.',
    cta: 'Open research',
    path: '/research',
  },
  {
    icon: Newspaper,
    title: 'Daily Brief',
    body: 'Turn legal updates into a daily ritual instead of a tab you forget.',
    cta: 'Open brief',
    path: '/brief',
  },
  {
    icon: BookOpen,
    title: 'Templates Vault',
    body: 'Start from polished structures when speed matters more than blank pages.',
    cta: 'Browse templates',
    path: '/templates',
  },
  {
    icon: Brain,
    title: 'Knowledge Memory',
    body: 'See what Advoq is learning so the assistant feels more yours over time.',
    cta: 'View memory',
    path: '/knowledge',
  },
];

const TRASH_ORIGIN_OPTIONS = ['ALL', 'CHAT', 'RESEARCH', 'JOLLY', 'WAR_ROOM', 'SAVED'];
const TRASH_ORIGIN_LABELS = {
  ALL: 'Everything',
  CHAT: 'Chat',
  RESEARCH: 'Research',
  JOLLY: 'Jolly',
  WAR_ROOM: 'Sabha',
  SAVED: 'Saved',
};
const TRASH_ENTITY_LABELS = {
  CHAT: 'Chat history',
  UPLOAD: 'Uploaded file',
  SAVED_ITEM: 'Saved item',
  JOLLY_SESSION: 'Jolly session',
  KNOWLEDGE_FILE: 'Matter file',
  MATTER: 'Sabha matter',
};

function SectionButton({ active, icon: Icon, label, hint, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-2xl p-3 text-left transition-all duration-150"
      style={{
        background: active ? 'var(--bg-elevated)' : 'var(--bg-surface)',
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
        boxShadow: active ? '0 18px 40px -32px rgba(0, 0, 0, 0.45)' : 'none',
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{
            background: active ? 'var(--bg-hover)' : 'var(--bg-elevated)',
            color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
          }}
        >
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold" style={{ color: active ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {label}
          </p>
          <p className="mt-1 text-xs leading-5" style={{ color: active ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
            {hint}
          </p>
        </div>
      </div>
    </button>
  );
}

function MetricCard({ icon: Icon, label, value, note }) {
  return (
    <div
      className="rounded-2xl p-4"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
    >
      <div
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ background: 'var(--bg-elevated)', color: 'var(--accent-primary)', border: '1px solid var(--border-subtle)' }}
      >
        <Icon size={18} />
      </div>
      <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{value}</p>
      <p className="mt-1 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      <p className="mt-2 text-xs leading-5" style={{ color: 'var(--text-muted)' }}>{note}</p>
    </div>
  );
}

function SelectChip({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="rounded-full px-3 py-2 text-sm font-medium transition-all duration-150"
      style={{
        background: active ? 'var(--bg-surface)' : 'var(--bg-elevated)',
        color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-default)'}`,
      }}
    >
      {label}
    </button>
  );
}

function ActionCard({ icon: Icon, title, body, cta, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-3xl p-5 text-left transition-all duration-150"
      style={{
        background: 'linear-gradient(145deg, var(--bg-surface) 0%, var(--bg-elevated) 100%)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-2xl"
          style={{ background: 'var(--bg-elevated)', color: 'var(--accent-primary)', border: '1px solid var(--border-subtle)' }}
        >
          <Icon size={18} />
        </div>
        <ArrowRight size={16} style={{ color: 'var(--text-muted)' }} />
      </div>
      <h3 className="mt-5 text-lg font-semibold" style={HEADER_FONT}>{title}</h3>
      <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{body}</p>
      <p className="mt-5 text-sm font-semibold" style={{ color: 'var(--accent-primary)' }}>{cta}</p>
    </button>
  );
}

function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, getAuthHeaders, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [section, setSection] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [integrationsOverview, setIntegrationsOverview] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsNotice, setIntegrationsNotice] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [institution, setInstitution] = useState('');
  const [language, setLanguage] = useState('ENGLISH');
  const [defaultMode, setDefaultMode] = useState('RESEARCH');
  const [defaultCourt, setDefaultCourt] = useState('Auto-detect from matter');
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [preferredCourts, setPreferredCourts] = useState([]);
  const [sidebarNavOrder, setSidebarNavOrder] = useState(DEFAULT_SIDEBAR_NAV_ORDER);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [trustedNewsSitesText, setTrustedNewsSitesText] = useState('');
  const [newsCitationsEnabled, setNewsCitationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashSubmitting, setTrashSubmitting] = useState(false);
  const [selectedTrashIds, setSelectedTrashIds] = useState([]);
  const [trashOriginFilter, setTrashOriginFilter] = useState('ALL');
  const [teamMemberships, setTeamMemberships] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamBusy, setTeamBusy] = useState(false);
  const [teamDrafts, setTeamDrafts] = useState({});
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [visibleLocalPassword, setVisibleLocalPassword] = useState('');
  const [showVisibleLocalPassword, setShowVisibleLocalPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newLocalPassword, setNewLocalPassword] = useState('');
  const [confirmLocalPassword, setConfirmLocalPassword] = useState('');
  const [googleChangeReady, setGoogleChangeReady] = useState(false);

  const parseTrustedNewsSites = () => (
    trustedNewsSitesText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  );

  useEffect(() => {
    setName(user?.name || '');
    setPhone(user?.phone || '');
    setInstitution(user?.institution || '');
    setLanguage(user?.language || 'ENGLISH');
    setDefaultMode(user?.default_chat_mode || 'RESEARCH');
    setDefaultCourt(user?.default_court || 'Auto-detect from matter');
    setPracticeAreas(user?.practice_areas || []);
    setPreferredCourts(user?.preferred_courts || []);
    setSidebarNavOrder(normalizeSidebarNavOrder(user?.sidebar_nav_order));
    setWhatsappOptIn(user?.whatsapp_opt_in || false);
    setTrustedNewsSitesText((user?.trusted_news_sites || []).join('\n'));
    setNewsCitationsEnabled(user?.news_citations_enabled !== false);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const requestedSection = params.get('section');
    if (requestedSection && SECTIONS.some((item) => item.id === requestedSection)) {
      setSection(requestedSection);
    }
  }, [location.search, user]);

  useEffect(() => {
    if (!user) return;
    setVisibleLocalPassword(readGeneratedLocalPassword(user.user_id));
    setShowVisibleLocalPassword(false);
    setGoogleChangeReady(Boolean(readPasswordVerification(user.user_id, 'CHANGE')));
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const loadPasswordStatus = async () => {
      try {
        const resp = await fetch(`${API}/auth/local-password/status`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!resp.ok) throw new Error('Failed to load password status');
        const data = await resp.json();
        if (!cancelled) setPasswordStatus(data);
      } catch {
        if (!cancelled) setPasswordStatus(null);
      }
    };

    loadPasswordStatus();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    if (params.get('password_action') !== 'reveal') return;

    const verificationId = readPasswordVerification(user.user_id, 'REVEAL');
    if (!verificationId) return;

    let cancelled = false;

    const revealAfterGoogleVerify = async () => {
      setPasswordBusy(true);
      try {
        const resp = await fetch(`${API}/auth/local-password/reveal`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({ verification_id: verificationId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.detail || 'Could not reveal password');
        if (cancelled) return;
        setVisibleLocalPassword(data.local_password || '');
        setShowVisibleLocalPassword(true);
        clearPasswordVerification(user.user_id, 'REVEAL');
        toast.success('Your local backup password is now visible.');
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Could not reveal password');
      } finally {
        if (!cancelled) {
          setPasswordBusy(false);
          const nextParams = new URLSearchParams(location.search);
          nextParams.delete('password_action');
          const nextSearch = nextParams.toString();
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
          );
        }
      }
    };

    revealAfterGoogleVerify();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, user]);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    if (params.get('password_action') !== 'change') return;
    const verificationId = readPasswordVerification(user.user_id, 'CHANGE');
    if (!verificationId) return;
    setGoogleChangeReady(true);
    toast.success('Google verification complete. Enter a new local password to finish.');
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('password_action');
    const nextSearch = nextParams.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
    );
  }, [location.search, user]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const fetchStats = async () => {
      setLoadingStats(true);
      try {
        const resp = await fetch(`${API}/user/stats`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (resp.ok && !cancelled) {
          setStats(await resp.json());
        }
      } catch {
      } finally {
        if (!cancelled) setLoadingStats(false);
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
    // `getAuthHeaders` is recreated by context on each render; this effect should only refetch on user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const resp = await fetch(`${API}/trash`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Failed to load trash');
      setTrashItems(await resp.json());
    } catch {
      toast.error('Failed to load trash');
    } finally {
      setTrashLoading(false);
    }
  };

  useEffect(() => {
    if (!user || section !== 'privacy') return;
    loadTrash();
    // `getAuthHeaders` is recreated by context on each render; this effect should only refetch on section/user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, user]);

  useEffect(() => {
    if (!user || section !== 'membership') return;
    let cancelled = false;

    const fetchTeams = async () => {
      setTeamsLoading(true);
      try {
        const resp = await fetch(`${API}/teams/my`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!resp.ok) throw new Error('Failed to load teams');
        const data = await resp.json();
        if (!cancelled) setTeamMemberships(data.teams || []);
      } catch {
        if (!cancelled) setTeamMemberships([]);
      } finally {
        if (!cancelled) setTeamsLoading(false);
      }
    };

    fetchTeams();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, user]);

  useEffect(() => {
    if (!user || !['integrations', 'governance'].includes(section)) return;
    let cancelled = false;

    const fetchIntegrationsOverview = async () => {
      setIntegrationsLoading(true);
      try {
        const { data, notice } = await loadIntegrationsOverview({
          apiBase: API,
          headers: getAuthHeaders(),
          user,
        });
        if (!cancelled) {
          setIntegrationsOverview(data);
          setIntegrationsNotice(notice);
        }
      } catch {
        if (!cancelled) {
          setIntegrationsOverview(null);
          setIntegrationsNotice('');
        }
      } finally {
        if (!cancelled) setIntegrationsLoading(false);
      }
    };

    fetchIntegrationsOverview();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, user]);

  useEffect(() => {
    setSelectedTrashIds(prev => prev.filter(id => trashItems.some(item => item.trash_id === id)));
  }, [trashItems]);

  const filteredTrashItems = trashOriginFilter === 'ALL'
    ? trashItems
    : trashItems.filter(item => item.origin === trashOriginFilter);
  const visibleTrashIds = filteredTrashItems.map(item => item.trash_id);

  const toggleTrashSelection = (trashId) => {
    setSelectedTrashIds(prev => (
      prev.includes(trashId)
        ? prev.filter(id => id !== trashId)
        : [...prev, trashId]
    ));
  };

  const permanentlyDeleteTrashItem = async (trashId, title) => {
    const label = title || 'this item';
    if (!window.confirm(`Permanently delete "${label}"? This cannot be undone.`)) return;

    setTrashSubmitting(true);
    try {
      const resp = await fetch(`${API}/trash/${trashId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Failed to delete');
      setTrashItems(prev => prev.filter(item => item.trash_id !== trashId));
      setSelectedTrashIds(prev => prev.filter(id => id !== trashId));
      toast.success(`"${label}" permanently deleted`);
    } catch {
      toast.error('Failed to permanently delete item');
    } finally {
      setTrashSubmitting(false);
    }
  };

  const purgeTrash = async ({ deleteAll = false } = {}) => {
    const trashIds = deleteAll ? [] : selectedTrashIds;
    if (!deleteAll && trashIds.length === 0) return;
    const confirmationText = deleteAll
      ? 'Permanently delete every item in trash? This cannot be undone.'
      : `Permanently delete ${trashIds.length} selected item${trashIds.length === 1 ? '' : 's'}? This cannot be undone.`;
    if (!window.confirm(confirmationText)) return;

    setTrashSubmitting(true);
    try {
      const resp = await fetch(`${API}/trash/purge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ trash_ids: trashIds, delete_all: deleteAll }),
      });
      if (!resp.ok) throw new Error('Failed to purge trash');
      const payload = await resp.json();
      if (deleteAll) {
        setTrashItems([]);
        setSelectedTrashIds([]);
      } else {
        setTrashItems(prev => prev.filter(item => !trashIds.includes(item.trash_id)));
        setSelectedTrashIds([]);
      }
      toast.success(`${payload.deleted_count || 0} item${payload.deleted_count === 1 ? '' : 's'} permanently deleted`);
    } catch {
      toast.error('Failed to permanently delete trash');
    } finally {
      setTrashSubmitting(false);
    }
  };

  const handleSave = async (updates, successMessage) => {
    setSaving(true);
    try {
      const resp = await fetch(`${API}/user/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(updates),
      });

      if (!resp.ok) {
        throw new Error('Failed to save');
      }

      const updated = await resp.json();
      setUser(updated);
      toast.success(successMessage || 'Settings saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const startGooglePasswordFlow = (intent) => {
    const params = new URLSearchParams({
      origin: window.location.origin,
      mode: 'login',
      intent,
      post_auth_path: `/settings?section=profile&password_action=${intent === 'reveal_local_password' ? 'reveal' : 'change'}`,
    });
    window.location.assign(`${API}/auth/google/start?${params.toString()}`);
  };

  const handleLocalPasswordChange = async () => {
    if (!newLocalPassword || !confirmLocalPassword) {
      toast.error('Enter and confirm the new password.');
      return;
    }
    if (newLocalPassword !== confirmLocalPassword) {
      toast.error('New password and confirmation do not match.');
      return;
    }
    if (newLocalPassword.length < 8) {
      toast.error('New password must be at least 8 characters long.');
      return;
    }

    const googleVerificationId = user ? readPasswordVerification(user.user_id, 'CHANGE') : '';
    if (!currentPassword.trim() && !googleVerificationId) {
      toast.error('Use your current password or verify with Google first.');
      return;
    }

    setPasswordBusy(true);
    try {
      const resp = await fetch(`${API}/auth/local-password/change`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword.trim() || undefined,
          verification_id: googleVerificationId || undefined,
          new_password: newLocalPassword,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Could not update password');
      setUser(data.user);
      setVisibleLocalPassword(data.local_password || newLocalPassword);
      setShowVisibleLocalPassword(false);
      setCurrentPassword('');
      setNewLocalPassword('');
      setConfirmLocalPassword('');
      if (user) {
        clearGeneratedLocalPassword(user.user_id);
        clearPasswordVerification(user.user_id, 'CHANGE');
      }
      setGoogleChangeReady(false);
      setPasswordStatus((prev) => ({
        ...(prev || {}),
        has_local_password: true,
        can_reveal_local_password: true,
        local_password_is_system_generated: false,
      }));
      toast.success('Local password updated.');
    } catch (error) {
      toast.error(error.message || 'Could not update password');
    } finally {
      setPasswordBusy(false);
    }
  };

  const toggleSelection = (value, setter) => {
    setter((prev) => (
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    ));
  };

  const downloadSnapshot = () => {
    const snapshot = {
      exported_at: new Date().toISOString(),
      account: {
        name,
        email: user?.email || '',
        phone,
        role: user?.role || '',
        plan: user?.plan || 'FREE',
        institution,
      },
      assistant_defaults: {
        language,
        default_chat_mode: defaultMode,
        default_court: defaultCourt,
        practice_areas: practiceAreas,
        preferred_courts: preferredCourts,
        sidebar_nav_order: sidebarNavOrder,
        whatsapp_opt_in: whatsappOptIn,
        theme,
      },
      usage: stats || {},
    };

    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `advoq-settings-snapshot-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Account snapshot downloaded');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const promoteTeamAdmin = async (teamId) => {
    const email = (teamDrafts[teamId] || '').trim();
    if (!email) return;
    setTeamBusy(true);
    try {
      const resp = await fetch(`${API}/teams/${teamId}/members/role`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ email, team_role: 'TEAM_ADMIN' }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || 'Failed to promote team admin');
      setTeamMemberships(prev => prev.map(team => (team.team_id === teamId ? data : team)));
      setTeamDrafts(prev => ({ ...prev, [teamId]: '' }));
      toast.success('Team admin updated');
    } catch (error) {
      toast.error(error.message || 'Failed to promote team admin');
    } finally {
      setTeamBusy(false);
    }
  };

  const rawPlanKey = (user?.plan || 'FREE').toUpperCase();
  const planKey = PLAN_META[rawPlanKey] ? rawPlanKey : (rawPlanKey === 'FREE' || rawPlanKey === 'PRO' || rawPlanKey === 'COMMUNITY' ? rawPlanKey : 'TEAM');
  const planMeta = PLAN_META[planKey] || PLAN_META.FREE;
  const score = stats?.personalisation_score || 0;
  const topPracticeAreas = Array.isArray(stats?.top_practice_areas) && stats.top_practice_areas.length
    ? stats.top_practice_areas
    : practiceAreas.map((area) => [area, 1]);
  const selectedMode = MODE_OPTIONS.find((mode) => mode.value === defaultMode) || MODE_OPTIONS[0];
  const workspaceLabel = user?.institution || (user?.role === 'STUDENT' ? 'Personal Study Hub' : 'Private Workspace');
  const orderedMovableNavItems = sidebarNavOrder
    .map((path) => SIDEBAR_MOVABLE_ITEMS.find((item) => item.path === path))
    .filter(Boolean);
  const quickStats = [
    {
      icon: Brain,
      label: 'AI Fit Score',
      value: loadingStats ? '...' : score,
      note: 'Higher scores mean sharper defaults, memory, and follow-through.',
    },
    {
      icon: TrendingUp,
      label: 'Chats',
      value: loadingStats ? '...' : stats?.total_chats || 0,
      note: 'Every chat strengthens your assistant’s working context.',
    },
    {
      icon: BookOpen,
      label: 'Documents',
      value: loadingStats ? '...' : stats?.total_documents || 0,
      note: 'Drafting activity compounds your document-level memory.',
    },
    {
      icon: Sparkles,
      label: 'Saved Items',
      value: loadingStats ? '...' : stats?.total_saved || 0,
      note: 'Reusable research becomes easier to revisit and remix.',
    },
  ];

  // Overview is replaced by the section-grid landing; no render needed
  const renderOverview = () => null;

  const renderProfile = () => (
    <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
      <div className="advoq-card p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Identity
            </p>
            <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Make the workspace feel unmistakably yours.</h2>
            <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
              The more precise your profile is, the easier it is for Advoq to pick the right tone, structure, and legal context.
            </p>
          </div>
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
          >
            <User size={20} />
          </div>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Full name
            </label>
            <input
              data-testid="settings-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="advoq-input w-full"
              placeholder="Your full name"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Email
            </label>
            <input value={user?.email || ''} disabled className="advoq-input w-full opacity-60" />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Phone
            </label>
            <input
              data-testid="settings-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="advoq-input w-full"
              placeholder="+91..."
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              {user?.role === 'STUDENT' ? 'College / university' : 'Firm / organisation'}
            </label>
            <input
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              className="advoq-input w-full"
              placeholder={user?.role === 'STUDENT' ? 'e.g. NLU Delhi' : 'e.g. Rao & Associates'}
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Role
            </label>
            <input value={user?.role || ''} disabled className="advoq-input w-full opacity-60" />
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Membership
            </label>
            <input value={user?.plan || 'FREE'} disabled className="advoq-input w-full opacity-60" />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            data-testid="save-profile-btn"
            onClick={() => handleSave({ name, phone, institution }, 'Profile updated')}
            disabled={saving}
            className="advoq-btn-primary flex items-center gap-2 text-sm"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save profile'}
          </button>
          <button onClick={() => setSection('experience')} className="advoq-btn-ghost text-sm">
            Continue to assistant setup
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div
          className="overflow-hidden rounded-[28px] p-6"
          style={{
            background: 'radial-gradient(circle at top right, var(--accent-glow), transparent 38%), linear-gradient(145deg, var(--bg-surface) 0%, var(--bg-elevated) 100%)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
            Signature card
          </p>
          <div className="mt-6 flex items-center gap-4">
            <div
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl text-2xl font-semibold"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              {name?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-xl font-semibold" style={HEADER_FONT}>{name || 'Your profile'}</h3>
              <p className="mt-1 truncate text-sm" style={{ color: 'var(--text-secondary)' }}>
                {user?.role || 'User'} {institution ? `· ${institution}` : ''}
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              { label: 'Primary language', value: language },
              { label: 'Default mode', value: selectedMode.label },
              { label: 'Workspace', value: workspaceLabel },
              { label: 'Plan', value: user?.plan || 'FREE' },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-2xl p-3"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  {item.label}
                </p>
                <p className="mt-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="advoq-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Sign-in methods
              </p>
              <h3 className="mt-2 text-xl font-semibold" style={HEADER_FONT}>Keep a fast local login alongside Google.</h3>
              <p className="mt-3 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                Google signups now get a local backup password. You can reveal it with Google verification, then change it later using either your current password or Google again.
              </p>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              <Lock size={20} />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {(passwordStatus?.auth_providers || user?.auth_providers || []).map((provider) => (
              <span
                key={provider}
                className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em]"
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
              >
                {provider}
              </span>
            ))}
          </div>

          <div
            className="mt-6 rounded-[24px] p-4"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Local password
            </p>
            <div className="mt-3 flex items-center gap-3">
              <input
                type={showVisibleLocalPassword ? 'text' : 'password'}
                value={visibleLocalPassword || 'temporary-hidden-password'}
                readOnly
                className="advoq-input w-full"
                style={{ letterSpacing: visibleLocalPassword ? '0.08em' : 'normal' }}
              />
              <button
                type="button"
                className="advoq-btn-ghost flex items-center gap-2 text-sm"
                disabled={!visibleLocalPassword}
                onClick={() => setShowVisibleLocalPassword((current) => !current)}
              >
                {showVisibleLocalPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                {showVisibleLocalPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="mt-3 text-xs leading-5" style={{ color: 'var(--text-muted)' }}>
              {visibleLocalPassword
                ? 'This is the current local password stored for non-Google sign-in on this account.'
                : passwordStatus?.can_reveal_local_password
                  ? 'Use Google verification to reveal the current local password if you need it.'
                  : 'No revealable local password is stored yet. Saving a new password below will create one.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                className="advoq-btn-primary text-sm"
                disabled={passwordBusy || !passwordStatus?.can_reveal_local_password}
                onClick={() => startGooglePasswordFlow('reveal_local_password')}
              >
                {passwordBusy ? 'Working...' : 'Verify with Google to reveal'}
              </button>
              {passwordStatus?.local_password_is_system_generated ? (
                <span className="rounded-full px-3 py-1 text-xs font-medium" style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
                  Auto-generated backup password
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid gap-4">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                Current password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="advoq-input w-full"
                placeholder="Enter current password, or verify with Google instead"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                New password
              </label>
              <input
                type="password"
                value={newLocalPassword}
                onChange={(event) => setNewLocalPassword(event.target.value)}
                className="advoq-input w-full"
                placeholder="Minimum 8 characters"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                Confirm new password
              </label>
              <input
                type="password"
                value={confirmLocalPassword}
                onChange={(event) => setConfirmLocalPassword(event.target.value)}
                className="advoq-input w-full"
                placeholder="Repeat the new password"
              />
            </div>
          </div>

          {googleChangeReady ? (
            <div className="mt-4 rounded-2xl p-3 text-sm" style={{ background: 'var(--accent-glow)', color: 'var(--text-primary)' }}>
              Google verification is active for this password change. You can save a new local password without entering the current one.
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              className="advoq-btn-primary text-sm"
              disabled={passwordBusy || !newLocalPassword || !confirmLocalPassword || (!currentPassword.trim() && !googleChangeReady)}
              onClick={handleLocalPasswordChange}
            >
              {passwordBusy ? 'Saving...' : 'Save local password'}
            </button>
            <button
              type="button"
              className="advoq-btn-ghost text-sm"
              disabled={passwordBusy}
              onClick={() => startGooglePasswordFlow('change_local_password')}
            >
              Verify with Google instead
            </button>
          </div>
        </div>

        <div className="advoq-card p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
            Why this matters
          </p>
          <div className="mt-4 space-y-3">
            {[
              'Your institution name shapes shared workspace identity and context.',
              'Your role nudges drafting style, matter framing, and explanation depth.',
              'A complete profile reduces repetitive setup across chat, documents, and research.',
            ].map((item) => (
              <div key={item} className="rounded-2xl p-3 text-sm leading-6" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderExperience = () => (
    <div className="space-y-6">
      <div className="advoq-card p-6 sm:p-7">
        <div className="flex flex-col gap-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Assistant defaults
              </p>
              <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Set the tone before every new matter starts.</h2>
              <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                This is where the product stops feeling generic. Pick the language, mode, court, and focus areas you want Advoq to assume.
              </p>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              <Brain size={20} />
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  Default language
                </label>
                <select
                  data-testid="settings-language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="advoq-input w-full"
                >
                  {LANGUAGES.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  Default mode
                </label>
                <select value={defaultMode} onChange={(e) => setDefaultMode(e.target.value)} className="advoq-input w-full">
                  {MODE_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  Default court / forum
                </label>
                <select value={defaultCourt} onChange={(e) => setDefaultCourt(e.target.value)} className="advoq-input w-full">
                  {COURT_OPTIONS.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className="rounded-3xl p-5"
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                    Appearance
                  </p>
                  <h3 className="mt-2 text-xl font-semibold" style={HEADER_FONT}>A theme you actually want to stay in.</h3>
                </div>
                <Palette size={18} style={{ color: 'var(--accent-primary)' }} />
              </div>

              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => theme !== 'light' && toggleTheme()}
                  className="flex-1 rounded-2xl p-4 text-left transition-all duration-150"
                  style={{
                    background: theme === 'light' ? 'var(--accent-glow)' : 'var(--bg-surface)',
                    border: `1px solid ${theme === 'light' ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  }}
                >
                  <Sun size={18} style={{ color: theme === 'light' ? 'var(--accent-primary)' : 'var(--text-secondary)' }} />
                  <p className="mt-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Light briefing</p>
                  <p className="mt-1 text-xs leading-5" style={{ color: 'var(--text-secondary)' }}>
                    Clean, calm, and better for long daytime sessions.
                  </p>
                </button>

                <button
                  onClick={() => theme !== 'dark' && toggleTheme()}
                  className="flex-1 rounded-2xl p-4 text-left transition-all duration-150"
                  style={{
                    background: theme === 'dark' ? 'var(--accent-glow)' : 'var(--bg-surface)',
                    border: `1px solid ${theme === 'dark' ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  }}
                >
                  <Moon size={18} style={{ color: theme === 'dark' ? 'var(--accent-primary)' : 'var(--text-secondary)' }} />
                  <p className="mt-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Night briefing</p>
                  <p className="mt-1 text-xs leading-5" style={{ color: 'var(--text-secondary)' }}>
                    Better for longer reading and focused late-session drafting.
                  </p>
                </button>
              </div>

              <div className="mt-5 rounded-2xl p-4" style={{ background: 'var(--bg-surface)' }}>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{selectedMode.label} mode is active by default.</p>
                <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{selectedMode.note}</p>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Practice focus
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {PRACTICE_AREA_OPTIONS.map((item) => (
                <SelectChip
                  key={item}
                  label={item}
                  active={practiceAreas.includes(item)}
                  onClick={() => toggleSelection(item, setPracticeAreas)}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Preferred forums
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {COURT_PREFERENCE_OPTIONS.map((item) => (
                <SelectChip
                  key={item}
                  label={item}
                  active={preferredCourts.includes(item)}
                  onClick={() => toggleSelection(item, setPreferredCourts)}
                />
              ))}
            </div>
          </div>

          <div
            className="rounded-3xl p-5"
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                  Navigation order
                </p>
                <h3 className="mt-2 text-xl font-semibold" style={HEADER_FONT}>Keep the operating spine pinned and tune the rest.</h3>
                <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                  Today, Board / Court Watch, Matters, Authorities, and Drafting stay locked at the top. Everything below can be reordered for your workflow.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSidebarNavOrder([...DEFAULT_SIDEBAR_NAV_ORDER])}
                className="advoq-btn-ghost text-sm"
              >
                Reset order
              </button>
            </div>

            <div className="mt-5 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-2xl p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  Pinned top
                </p>
                <div className="mt-3 grid gap-2">
                  {SIDEBAR_FIXED_ITEMS.map((item, index) => (
                    <div
                      key={item.path}
                      className="flex items-center justify-between rounded-2xl px-3 py-3"
                      style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={16} style={{ color: 'var(--accent-primary)' }} />
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.label}</span>
                      </div>
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>#{index + 1}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl p-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  Movable below
                </p>
                <div className="mt-3 grid gap-2">
                  {orderedMovableNavItems.map((item, index) => (
                    <div
                      key={item.path}
                      className="flex items-center gap-3 rounded-2xl px-3 py-3"
                      style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
                    >
                      <item.icon size={16} style={{ color: 'var(--text-secondary)' }} />
                      <span className="flex-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {item.label}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSidebarNavOrder((prev) => {
                            const next = [...prev];
                            if (index === 0) return next;
                            [next[index - 1], next[index]] = [next[index], next[index - 1]];
                            return next;
                          })}
                          disabled={index === 0}
                          className="app-sidebar__icon-button"
                          title={`Move ${item.label} up`}
                          style={{ opacity: index === 0 ? 0.45 : 1 }}
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setSidebarNavOrder((prev) => {
                            const next = [...prev];
                            if (index === prev.length - 1) return next;
                            [next[index + 1], next[index]] = [next[index], next[index + 1]];
                            return next;
                          })}
                          disabled={index === orderedMovableNavItems.length - 1}
                          className="app-sidebar__icon-button"
                          title={`Move ${item.label} down`}
                          style={{ opacity: index === orderedMovableNavItems.length - 1 ? 0.45 : 1 }}
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => handleSave({
                language,
                default_chat_mode: defaultMode,
                default_court: defaultCourt,
                practice_areas: practiceAreas,
                preferred_courts: preferredCourts,
                sidebar_nav_order: sidebarNavOrder,
              }, 'Assistant setup updated')}
              disabled={saving}
              className="advoq-btn-primary flex items-center gap-2 text-sm"
            >
              <Save size={14} />
              {saving ? 'Saving...' : 'Save assistant setup'}
            </button>
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Theme changes apply instantly. Agent order and workspace defaults save to your account.
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
      <div className="advoq-card p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Daily rhythm
            </p>
            <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Build a notification habit users actually value.</h2>
            <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
              Keep the signal high and the noise low. Daily Brief is the stickiest loop in the product when it lands in the right language and cadence.
            </p>
          </div>
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
          >
            <Bell size={20} />
          </div>
        </div>

        <div className="mt-8 rounded-[28px] p-5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
                style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
              >
                <Smartphone size={18} />
              </div>
              <div>
                <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>WhatsApp Daily Brief</p>
                <p className="mt-1 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                  Receive 3 fresh Supreme Court judgments every morning at 8 AM, adapted to your workspace language.
                </p>
              </div>
            </div>

            <button
              onClick={() => setWhatsappOptIn((prev) => !prev)}
              className="rounded-full px-4 py-2 text-sm font-semibold transition-all duration-150"
              style={{
                background: whatsappOptIn ? 'var(--accent-glow)' : 'var(--bg-surface)',
                color: whatsappOptIn ? 'var(--accent-primary)' : 'var(--text-secondary)',
                border: `1px solid ${whatsappOptIn ? 'var(--accent-primary)' : 'var(--border-default)'}`,
              }}
            >
              {whatsappOptIn ? 'Enabled' : 'Enable'}
            </button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {[
              { label: 'Delivery', value: '8:00 AM' },
              { label: 'Language', value: language },
              { label: 'Status', value: whatsappOptIn ? 'Active' : 'Paused' },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl p-3" style={{ background: 'var(--bg-surface)' }}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  {item.label}
                </p>
                <p className="mt-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => handleSave({
                whatsapp_opt_in: whatsappOptIn,
                trusted_news_sites: parseTrustedNewsSites(),
                news_citations_enabled: newsCitationsEnabled,
              }, 'Briefing preferences updated')}
              disabled={saving}
              className="advoq-btn-primary flex items-center gap-2 text-sm"
            >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save briefing setup'}
          </button>
          <button onClick={() => navigate('/brief')} className="advoq-btn-ghost text-sm">
            Open Daily Brief
          </button>
        </div>
      </div>

        <div className="space-y-6">
          <div className="advoq-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              News bundle controls
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Trusted news websites (one per line)
                </p>
                <textarea
                  value={trustedNewsSitesText}
                  onChange={(e) => setTrustedNewsSitesText(e.target.value)}
                  rows={5}
                  className="advoq-input w-full"
                  placeholder={'https://www.livelaw.in/\nhttps://www.barandbench.com/'}
                />
              </div>
              <button
                onClick={() => setNewsCitationsEnabled((prev) => !prev)}
                className="rounded-full px-4 py-2 text-sm font-semibold transition-all duration-150"
                style={{
                  background: newsCitationsEnabled ? 'var(--accent-glow)' : 'var(--bg-surface)',
                  color: newsCitationsEnabled ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  border: `1px solid ${newsCitationsEnabled ? 'var(--accent-primary)' : 'var(--border-default)'}`,
                }}
              >
                News citations: {newsCitationsEnabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          </div>

          <div className="advoq-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Make it sticky
          </p>
          <div className="mt-4 space-y-3">
            {[
              'Use one notification surface with obvious value instead of stacking channels.',
              'Keep your assistant language aligned with your briefing language for continuity.',
              'Open the brief, save the best items, then continue the thread in research or chat.',
            ].map((item) => (
              <div key={item} className="rounded-2xl p-3 text-sm leading-6" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
          <ActionCard
            icon={Newspaper}
            title="Generate today’s brief"
            body="Use the briefing page as the top of your daily legal workflow."
            cta="Open briefing"
            onClick={() => navigate('/brief')}
          />
          <ActionCard
            icon={Rocket}
            title="Turn updates into research"
            body="Move from a headline to a deeper investigation while the context is still warm."
            cta="Open research"
            onClick={() => navigate('/research')}
          />
        </div>
      </div>
    </div>
  );

  const renderMembership = () => (
    <div className="space-y-6">
      <div
        className="overflow-hidden rounded-[32px] p-6 sm:p-7"
        style={{
          background: `radial-gradient(circle at top right, ${planMeta.accent}, transparent 40%), linear-gradient(145deg, var(--bg-surface) 0%, var(--bg-elevated) 100%)`,
          border: '1px solid var(--border-subtle)',
        }}
      >
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em]"
                style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
              >
                Current membership
              </span>
              <span
                className="rounded-full px-3 py-1 text-xs font-medium"
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
              >
                {user?.plan || 'FREE'}
              </span>
            </div>

            <h2 className="mt-5 text-3xl font-semibold" style={HEADER_FONT}>{planMeta.title}</h2>
            <p className="mt-3 max-w-2xl text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
              {planMeta.strap}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => navigate('/templates')} className="advoq-btn-primary text-sm">
                Explore premium-style templates
              </button>
              <button onClick={() => navigate('/research')} className="advoq-btn-ghost text-sm">
                Open Research Workspace
              </button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
            {planMeta.features.map((item) => (
              <div
                key={item}
                className="rounded-3xl p-4"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
              >
                <Crown size={16} style={{ color: 'var(--accent-primary)' }} />
                <p className="mt-4 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {TIER_CARDS.map((item) => {
          const active = item.key === planKey;
          return (
            <div
              key={item.key}
              className="rounded-[28px] p-6"
              style={{
                background: active ? 'linear-gradient(145deg, var(--accent-glow) 0%, var(--bg-surface) 100%)' : 'var(--bg-surface)',
                border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.title}</p>
                  <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{item.summary}</p>
                </div>
                {active && (
                  <span
                    className="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em]"
                    style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
                  >
                    Current
                  </span>
                )}
              </div>

              <div className="mt-5 space-y-3">
                {item.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="advoq-card p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Team access
            </p>
            <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>See your firm setup without exposing anyone’s work.</h2>
            <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
              Team admins can promote members into team-admin access. Demoting a team admin still requires an Advoq owner or Advoq admin.
            </p>
          </div>
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
          >
            <Building2 size={20} />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {teamsLoading ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading team access…</p>
          ) : teamMemberships.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>You are not mapped to a team yet.</p>
          ) : teamMemberships.map((team) => {
            const currentMembership = (team.members || []).find((member) => String(member.email || '').toLowerCase() === String(user?.email || '').toLowerCase());
            const canPromote = user?.admin_console_access || currentMembership?.team_role === 'TEAM_ADMIN';
            return (
              <div
                key={team.team_id}
                className="rounded-[28px] p-5"
                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{team.name}</p>
                    <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                      Your role: {currentMembership?.team_role || 'MEMBER'}
                    </p>
                  </div>
                  <span className="rounded-full px-3 py-1 text-xs font-medium" style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}>
                    {(team.members || []).length} members
                  </span>
                </div>

                <div className="mt-4 grid gap-3">
                  {(team.members || []).map((member) => (
                    <div key={`${team.team_id}-${member.email}`} className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: 'var(--bg-surface)' }}>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{member.email}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{member.team_role}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {canPromote ? (
                  <div className="mt-4 grid gap-3 md:grid-cols-[1fr_180px]">
                    <input
                      className="advoq-input"
                      value={teamDrafts[team.team_id] || ''}
                      onChange={(event) => setTeamDrafts(prev => ({ ...prev, [team.team_id]: event.target.value }))}
                      placeholder="Promote member email to team admin"
                    />
                    <button
                      type="button"
                      className="advoq-btn-primary"
                      disabled={teamBusy || !(teamDrafts[team.team_id] || '').trim()}
                      onClick={() => promoteTeamAdmin(team.team_id)}
                    >
                      Promote to Team Admin
                    </button>
                  </div>
                ) : (
                  <p className="mt-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                    Promotions inside this team are controlled by your current team admin or the Advoq staff console.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderPrivacy = () => (
    <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
      <div className="space-y-6">
        <div className="advoq-card p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Exports
              </p>
              <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Give users control they can actually use.</h2>
              <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                No dead-end buttons here. You can download a live account snapshot, review what the workspace remembers, and exit safely from this device.
              </p>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              <Shield size={20} />
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <button
              onClick={downloadSnapshot}
              className="rounded-[28px] p-5 text-left transition-all duration-150"
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
            >
              <Download size={18} style={{ color: 'var(--accent-primary)' }} />
              <p className="mt-4 text-lg font-semibold" style={HEADER_FONT}>Download account snapshot</p>
              <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                Export profile details, assistant defaults, and live usage stats as JSON.
              </p>
            </button>

            <button
              onClick={() => navigate('/knowledge')}
              className="rounded-[28px] p-5 text-left transition-all duration-150"
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
            >
              <Brain size={18} style={{ color: 'var(--accent-primary)' }} />
              <p className="mt-4 text-lg font-semibold" style={HEADER_FONT}>Review knowledge memory</p>
              <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                See how your profile, activity, and matter mix are shaping the assistant.
              </p>
            </button>
          </div>
        </div>

        <div className="advoq-card p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Permanent trash
              </p>
              <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Delete once now, destroy forever later.</h2>
              <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                Chats, research files, Jolly sessions, and Sabha items land here first. Choose what to permanently erase or clear everything forever in one move.
              </p>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              <Trash2 size={20} />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {TRASH_ORIGIN_OPTIONS.map((origin) => (
              <SelectChip
                key={origin}
                active={trashOriginFilter === origin}
                label={TRASH_ORIGIN_LABELS[origin]}
                onClick={() => setTrashOriginFilter(origin)}
              />
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button onClick={loadTrash} className="advoq-btn-ghost text-sm" disabled={trashLoading || trashSubmitting}>
              {trashLoading ? 'Refreshing...' : 'Refresh trash'}
            </button>
            <button
              onClick={() => purgeTrash()}
              disabled={trashSubmitting || selectedTrashIds.length === 0}
              className="advoq-btn-primary flex items-center gap-2 text-sm"
              style={{ opacity: selectedTrashIds.length === 0 ? 0.55 : 1 }}
            >
              <Trash2 size={14} />
              Delete selected forever
            </button>
            <button
              onClick={() => purgeTrash({ deleteAll: true })}
              disabled={trashSubmitting || trashItems.length === 0}
              className="advoq-btn-ghost text-sm"
              style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.28)' }}
            >
              Delete all forever
            </button>
            <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              {trashItems.length} item{trashItems.length === 1 ? '' : 's'} in trash
            </span>
          </div>

          {filteredTrashItems.length > 0 && (
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => {
                  const nextVisibleIds = visibleTrashIds.filter(id => !selectedTrashIds.includes(id));
                  if (nextVisibleIds.length === 0) {
                    setSelectedTrashIds(prev => prev.filter(id => !visibleTrashIds.includes(id)));
                    return;
                  }
                  setSelectedTrashIds(prev => [...new Set([...prev, ...visibleTrashIds])]);
                }}
                className="text-sm"
                style={{ color: 'var(--accent-primary)' }}
              >
                {visibleTrashIds.every(id => selectedTrashIds.includes(id)) ? 'Clear visible selection' : 'Select visible items'}
              </button>
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {selectedTrashIds.length} selected
              </span>
            </div>
          )}

          <div className="mt-6 space-y-3">
            {trashLoading ? (
              <div className="rounded-3xl p-5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                Loading trash…
              </div>
            ) : filteredTrashItems.length === 0 ? (
              <div className="rounded-3xl p-5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Trash is empty.</p>
                <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                  Items you delete from chat, research, Jolly, and Sabha will collect here until you permanently erase them.
                </p>
              </div>
            ) : (
              filteredTrashItems.map((item) => (
                <div
                  key={item.trash_id}
                  className="rounded-3xl p-4"
                  style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedTrashIds.includes(item.trash_id)}
                      onChange={() => toggleTrashSelection(item.trash_id)}
                      className="mt-1 h-4 w-4"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {item.title || 'Untitled'}
                        </p>
                        <span
                          className="rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]"
                          style={{ background: 'var(--bg-surface)', color: 'var(--accent-primary)' }}
                        >
                          {TRASH_ENTITY_LABELS[item.entity_type] || item.entity_type}
                        </span>
                        <span
                          className="rounded-full px-2.5 py-1 text-[11px] font-medium"
                          style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}
                        >
                          {TRASH_ORIGIN_LABELS[item.origin] || item.origin || 'Workspace'}
                        </span>
                      </div>
                      {item.subtitle ? (
                        <p className="mt-1 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                          {item.subtitle}
                        </p>
                      ) : null}
                      <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                        Trashed {item.trashed_at ? new Date(item.trashed_at).toLocaleString() : 'recently'}
                      </p>
                    </div>
                    <button
                      onClick={() => permanentlyDeleteTrashItem(item.trash_id, item.title)}
                      disabled={trashSubmitting}
                      className="advoq-btn-ghost text-sm"
                      style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.24)' }}
                    >
                      Delete forever
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="advoq-card p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
            Session safety
          </p>
          <div className="mt-4 rounded-[24px] p-5" style={{ background: 'var(--bg-elevated)' }}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Sign out from this device</p>
                <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
                  Use this when you are done on a shared laptop or want a clean session reset.
                </p>
              </div>
              <button onClick={handleLogout} className="advoq-btn-ghost flex items-center gap-2 text-sm">
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="advoq-card p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
            Visibility map
          </p>
          <div className="mt-4 space-y-3">
            {[
              {
                icon: Globe,
                title: 'Profile identity',
                body: 'Your name, role, institution, and language help the workspace tailor tone and defaults.',
              },
              {
                icon: Target,
                title: 'Practice signals',
                body: 'Selected courts, focus areas, and chat activity improve relevance across matters.',
              },
              {
                icon: Building2,
                title: 'Workspace memory',
                body: 'Saved items, documents, and research strengthen continuity over time.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                <div className="flex items-start gap-3">
                  <item.icon size={16} className="mt-1 shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.title}</p>
                    <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{item.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
          <ActionCard
            icon={BookOpen}
            title="Open saved library"
            body="Review the material you have kept close for later arguments and drafting."
            cta="Open saved"
            onClick={() => navigate('/saved')}
          />
          <ActionCard
            icon={Scale}
            title="Refine legal memory"
            body="The sharper your profile and knowledge graph, the less setup each new matter needs."
            cta="Open profile memory"
            onClick={() => navigate('/knowledge')}
          />
        </div>
      </div>
    </div>
  );

  const renderIntegrations = () => {
    const integrations = integrationsOverview?.integrations || [];
    const liveCount = integrations.filter((item) => item.mode === 'live').length;
    const scaffoldCount = integrations.filter((item) => item.mode === 'scaffolded').length;
    const manualCount = integrations.filter((item) => item.mode === 'manual').length;
    const verifiedPublicLaw = integrationsOverview?.verified_public_law || {};

    return (
      <div className="space-y-6">
        <div className="advoq-card p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Integration-ready architecture
              </p>
              <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Know what is live, what is manual, and what is scaffolded.</h2>
              <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                Advoq keeps delivery and document hooks honest. We only show live connections when credentials exist; everything else stays clearly marked as manual or scaffolded.
              </p>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              <Globe size={20} />
            </div>
          </div>
          <div className="mt-5">
            <WorkflowCompatibilityNotice message={integrationsNotice} />
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <MetricCard icon={CheckCircle2} label="Live" value={liveCount} note="Connected integrations that are active for this workspace." />
            <MetricCard icon={Rocket} label="Scaffolded" value={scaffoldCount} note="Product surfaces and service boundaries are ready even if credentials are missing." />
            <MetricCard icon={Smartphone} label="Manual" value={manualCount} note="Workflows are usable now with explicit manual follow-through." />
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="advoq-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                  Connected surfaces
                </p>
                <h3 className="mt-2 text-xl font-semibold" style={HEADER_FONT}>External hooks around the daily legal loop.</h3>
              </div>
              <Building2 size={18} style={{ color: 'var(--accent-primary)' }} />
            </div>

            <div className="mt-5 space-y-3">
              {integrationsLoading ? (
                <div className="rounded-2xl p-4 text-sm" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  Loading integration statuses…
                </div>
              ) : null}
              {!integrationsLoading && !integrations.length ? (
                <div className="rounded-2xl p-4 text-sm" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  Integration scaffolds are not loaded yet.
                </div>
              ) : null}
              {integrations.map((item) => (
                <div key={item.id} className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                      <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
                    </div>
                    <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: 'var(--bg-surface)', color: 'var(--accent-primary)', border: '1px solid var(--border-subtle)' }}>
                      {item.status_label}
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <span className="app-page-chip">{item.surface}</span>
                    <span className="app-page-chip">{item.mode.replace(/_/g, ' ')}</span>
                    {item.connected_at ? <span className="app-page-chip">Connected {new Date(item.connected_at).toLocaleDateString('en-IN')}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="advoq-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Delivery surfaces
              </p>
              <h3 className="mt-2 text-xl font-semibold" style={HEADER_FONT}>Product lanes already wired around those integrations.</h3>
              <div className="mt-5 space-y-3">
                {(integrationsOverview?.delivery_surfaces || []).map((item) => (
                  <div key={item.id} className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                    <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>{item.mode.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="advoq-card p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Verified public law scaffold
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{verifiedPublicLaw.source_family_count || 0}</p>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Official source families</p>
                </div>
                <div className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{verifiedPublicLaw.adapter_ready_count || 0}</p>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Adapters ready</p>
                </div>
                <div className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{(verifiedPublicLaw.material_types_supported || []).length || 0}</p>
                  <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Material types</p>
                </div>
              </div>
              <div className="mt-5 flex gap-3 flex-wrap">
                <button onClick={() => navigate('/authorities')} className="advoq-btn-primary text-sm">Open authorities</button>
                <button onClick={() => navigate('/client-loop')} className="advoq-btn-ghost text-sm">Open client loop</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGovernance = () => {
    const governance = integrationsOverview?.governance || {};
    const verifiedPublicLaw = integrationsOverview?.verified_public_law || {};

    return (
      <div className="space-y-6">
        <div className="advoq-card p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                Governance rails
              </p>
              <h2 className="mt-2 text-2xl font-semibold" style={HEADER_FONT}>Trust and provenance stay explicit across the platform.</h2>
              <p className="mt-3 text-sm leading-7" style={{ color: 'var(--text-secondary)' }}>
                These rules keep Advoq serious: verified public law is separated from matter files, reviewed memory, manual notes, and inference, and manual integrations stay honestly labeled.
              </p>
            </div>
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}
            >
              <Shield size={20} />
            </div>
          </div>
          <div className="mt-5">
            <WorkflowCompatibilityNotice message={integrationsNotice} />
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              { title: 'Trust rule', body: governance.trust_rule || 'Verified means official or policy-approved only.' },
              { title: 'Manual rule', body: governance.manual_rule || 'Manual flows stay clearly labeled.' },
              { title: 'Audit rule', body: governance.audit_rule || 'Private matter memory stays distinct from public law.' },
            ].map((item) => (
              <div key={item.title} className="rounded-3xl p-5" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{item.title}</p>
                <p className="mt-3 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
          <div className="advoq-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Verification posture
            </p>
            <div className="mt-5 space-y-3">
              {[
                'Verified primary means official court, statute, gazette, or regulator text.',
                'Verified secondary support still needs forum-aware use and treatment review.',
                'Portal-only records can support status and procedure, not silent legal conclusions.',
                'Matter files, reviewed memory, and chamber notes stay useful but separate from public-law verification.',
              ].map((item) => (
                <div key={item} className="rounded-2xl p-4 text-sm leading-6" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="advoq-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
              Public-law scaffold
            </p>
            <p className="mt-2 text-sm leading-6" style={{ color: 'var(--text-secondary)' }}>
              The verified corpus layer already reserves room for courts, statutes, gazettes, and regulators before any connector is treated as authoritative.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{verifiedPublicLaw.source_family_count || 0}</p>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Source families</p>
              </div>
              <div className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{verifiedPublicLaw.adapter_ready_count || 0}</p>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Adapters ready</p>
              </div>
              <div className="rounded-2xl p-4" style={{ background: 'var(--bg-elevated)' }}>
                <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{(verifiedPublicLaw.material_types_supported || []).length || 0}</p>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Material types</p>
              </div>
            </div>
            <div className="mt-5 flex gap-3 flex-wrap">
              <button onClick={() => navigate('/authorities')} className="advoq-btn-primary text-sm">Open authorities</button>
              <button onClick={() => setSection('integrations')} className="advoq-btn-ghost text-sm">Review integrations</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = () => {
    if (section === 'profile') return renderProfile();
    if (section === 'experience') return renderExperience();
    if (section === 'notifications') return renderNotifications();
    if (section === 'integrations') return renderIntegrations();
    if (section === 'governance') return renderGovernance();
    if (section === 'membership') return renderMembership();
    if (section === 'privacy') return renderPrivacy();
    return renderOverview();
  };

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="settings-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <AppPageHeader
          eyebrow="Settings"
          title={`${user?.name || 'Your workspace'} · ${user?.plan || 'FREE'}`}
          actions={section !== 'overview' ? (
            <button onClick={() => setSection('overview')} className="advoq-btn-ghost" style={{ fontSize: '12px' }}>
              ← Back
            </button>
          ) : null}
        />

        {section === 'overview' ? (
          /* Fixed non-scrollable landing — section buttons only */
          <div style={{
            flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '24px 20px', overflow: 'hidden',
          }}>
            <div style={{ width: '100%', maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Quick status pills */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                {[
                  { label: 'Mode', value: selectedMode.label },
                  { label: 'Theme', value: theme === 'dark' ? 'Night' : 'Light' },
                  { label: 'Language', value: language },
                  { label: 'Daily Brief', value: whatsappOptIn ? 'On' : 'Off' },
                ].map(item => (
                  <span key={item.label} style={{
                    fontSize: '11px', padding: '4px 10px', borderRadius: '999px',
                    background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                    color: 'var(--text-secondary)', fontWeight: 600,
                  }}>
                    {item.label}: <span style={{ color: 'var(--text-primary)' }}>{item.value}</span>
                  </span>
                ))}
              </div>

              {SECTIONS.filter(s => s.id !== 'overview').map(item => (
                <SectionButton
                  key={item.id}
                  active={false}
                  icon={item.icon}
                  label={item.label}
                  hint={item.hint}
                  onClick={() => setSection(item.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          /* Section content — scrollable */
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
              {renderSection()}
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

export default SettingsPage;
